import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  MessageSquareText,
  SearchCheck,
  Network,
  FileCheck2,
  BadgeDollarSign,
  ClipboardCheck,
  Factory,
  ShieldCheck,
  Container,
  Ship,
} from "lucide-react";
import SEO from "../components/SEO";
import { Reveal, Stagger, StaggerItem, SectionLabel } from "../components/Motion";
import { IMAGES } from "../lib/site";
import { useLightbox, Watermark } from "../components/Lightbox";

const STEPS = [
  { icon: MessageSquareText, title: "Buyer Inquiry", copy: "You share the product, specification, quantity, packaging and destination port you need." },
  { icon: SearchCheck, title: "Requirement Review", copy: "We review your requirements and clarify technical details, documentation needs and timeline." },
  { icon: Network, title: "Supplier Matching", copy: "We match your requirement with suitable selected Indonesian manufacturing partners." },
  { icon: FileCheck2, title: "Specification / Sample", copy: "Specifications are confirmed and samples or COA can be arranged for your review and approval." },
  { icon: BadgeDollarSign, title: "Commercial Quotation", copy: "You receive a clear quotation based on confirmed specification, volume, packaging and incoterm (FOB / CIF)." },
  { icon: ClipboardCheck, title: "Order Confirmation", copy: "Terms, specification and payment structure are confirmed before production begins." },
  { icon: Factory, title: "Production", copy: "The selected manufacturing partner produces your order according to the agreed specification." },
  { icon: ShieldCheck, title: "Quality & Documentation", copy: "Pre-shipment checks and export documentation are coordinated with the supplier." },
  { icon: Container, title: "Container Loading", copy: "Container loading is coordinated and documented before dispatch." },
  { icon: Ship, title: "Export Shipment", copy: "Freight coordination and shipment to your destination port." },
];

export default function ExportProcess() {
  const openLightbox = useLightbox();
  return (
    <>
      <SEO />
      <div data-testid="export-process-page" className="bg-charcoal text-offwhite min-h-screen">
        <section className="pt-40 pb-16 sm:pb-20 tech-grid">
          <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
            <SectionLabel index="PROCESS" title="Export Workflow" dark />
            <Reveal className="mt-8 max-w-4xl">
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.98]">
                From Indonesian Factory to Your Destination
              </h1>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 max-w-2xl text-base sm:text-lg text-offwhite/60 leading-relaxed">
                A structured, transparent export workflow — coordinated by HAQILA together with selected manufacturing
                partners across Indonesia.
              </p>
            </Reveal>
          </div>
        </section>

        <section className="pb-24 sm:pb-32">
          <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
            <div className="relative">
              <span className="hidden lg:block absolute left-[27px] top-2 bottom-2 w-px bg-white/10" />
              <Stagger gap={0.07}>
                {STEPS.map((step, i) => (
                  <StaggerItem key={step.title}>
                    <div
                      data-testid={`export-step-${i + 1}`}
                      className="group relative grid lg:grid-cols-12 gap-4 lg:gap-8 items-start py-8 lg:py-10 border-t border-white/10"
                    >
                      <div className="lg:col-span-2 flex lg:flex-col items-center lg:items-start gap-4">
                        <span className="relative z-10 w-14 h-14 shrink-0 bg-charcoal border border-white/15 group-hover:border-clay flex items-center justify-center transition-colors duration-500">
                          <step.icon className="w-6 h-6 text-clay" strokeWidth={1.5} />
                        </span>
                      </div>
                      <div className="lg:col-span-4">
                        <span className="font-display text-xs font-semibold tracking-[0.3em] text-offwhite/35">
                          STEP {String(i + 1).padStart(2, "0")}
                        </span>
                        <h2 className="mt-2 font-display text-2xl font-bold tracking-tight">{step.title}</h2>
                      </div>
                      <p className="lg:col-span-6 text-sm sm:text-base text-offwhite/55 leading-relaxed lg:pt-6">
                        {step.copy}
                      </p>
                    </div>
                  </StaggerItem>
                ))}
              </Stagger>
            </div>

            <Reveal className="mt-20 grid lg:grid-cols-12 gap-10 items-center border border-white/10">
              <div className="lg:col-span-7 p-10 sm:p-14">
                <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
                  Ready to start the process?
                </h2>
                <p className="mt-4 text-sm text-offwhite/60 leading-relaxed max-w-lg">
                  Send us your product, quantity and destination requirements — we respond with sourcing options and a
                  quotation path.
                </p>
                <Link
                  to="/request-a-quote"
                  data-testid="export-process-quote-cta"
                  className="group mt-8 inline-flex items-center gap-3 bg-forest hover:bg-moss text-offwhite font-semibold px-8 py-4 text-sm tracking-wide transition-colors duration-300"
                >
                  Request a Quote
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
              <div className="lg:col-span-5 relative h-64 lg:h-full min-h-[280px]">
                <div
                  className="absolute inset-0 cursor-zoom-in"
                  onClick={() =>
                    openLightbox(
                      [{ src: IMAGES.containers, alt: "Shipping containers ready for export", caption: "Shipping containers ready for export" }],
                      0
                    )
                  }
                >
                  <img
                    src={IMAGES.containers}
                    alt="Shipping containers ready for export"
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover img-spotlight"
                  />
                  <Watermark />
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      </div>
    </>
  );
}
